/**
 * Mock Data Utilities for Content Studio
 * Provides realistic mock data for citations, topic suggestions, etc.
 */

import type { ReferenceLink, TopicSuggestion } from '@/types'

// Mock citation database
const MOCK_CITATIONS = [
  {
    id: 'cit_001',
    title: 'The Complete Guide to GEO in 2024',
    url: 'https://blog.techcorp.io/geo-guide-2024',
    domain: 'blog.techcorp.io',
    source: 'blog' as const,
    snippet: 'Understanding Google Experience Optimization is crucial for modern content strategy...',
  },
  {
    id: 'cit_002',
    title: 'Reddit discussion: How AI ranks content',
    url: 'https://reddit.com/r/SEO/comments/abc123/how_ai_ranks',
    domain: 'reddit.com',
    source: 'reddit' as const,
    snippet: 'Discussion on how AI search engines determine content ranking...',
  },
  {
    id: 'cit_003',
    title: '10 SEO Tips That Actually Work',
    url: 'https://medium.com/@expert/seo-tips-2024',
    domain: 'medium.com',
    source: 'blog' as const,
    snippet: 'After analyzing 1000+ articles, here are the proven SEO strategies...',
  },
  {
    id: 'cit_004',
    title: 'Content Strategy Framework Explained',
    url: 'https://dev.to/author/content-framework',
    domain: 'dev.to',
    source: 'blog' as const,
    snippet: 'A comprehensive framework for building content that ranks...',
  },
  {
    id: 'cit_005',
    title: 'Reddit AMA: Former Google Search Engineer',
    url: 'https://reddit.com/r/IAMA/comments/def456/google_search_ama',
    domain: 'reddit.com',
    source: 'reddit' as const,
    snippet: 'I spent 5 years working on Google Search. Ask me anything...',
  },
  {
    id: 'cit_006',
    title: 'AI Content Detection: What Works',
    url: 'https://blog.semoz.com/ai-detection-guide',
    domain: 'blog.semoz.com',
    source: 'blog' as const,
    snippet: 'How AI search engines identify and rank high-quality content...',
  },
  {
    id: 'cit_007',
    title: 'The Future of Search is AI-First',
    url: 'https://techcrunch.com/2024/01/15/ai-search-future',
    domain: 'techcrunch.com',
    source: 'blog' as const,
    snippet: 'Why traditional SEO is dead and what comes next...',
  },
  {
    id: 'cit_008',
    title: 'Reddit tips for content creators',
    url: 'https://reddit.com/r/ContentCreation/comments/ghi789/tips_guide',
    domain: 'reddit.com',
    source: 'reddit' as const,
    snippet: 'Community tips for creating content that resonates...',
  },
]

/**
 * Generate mock reference links from selected prompts
 * Simulates auto-import from prompt citations
 */
export function generateMockReferenceLinks(promptIds: string[]): ReferenceLink[] {
  const links: ReferenceLink[] = []

  promptIds.forEach((promptId, idx) => {
    // Select 2-3 random citations per prompt
    const numCitations = Math.floor(Math.random() * 2) + 2
    const startIndex = (idx * 2) % MOCK_CITATIONS.length

    for (let i = 0; i < numCitations; i++) {
      const citation = MOCK_CITATIONS[(startIndex + i) % MOCK_CITATIONS.length]
      links.push({
        ...citation,
        id: `ref_${Date.now()}_${promptId}_${i}`,
        source: 'auto',
        promptId,
        autoImported: true,
      })
    }
  })

  return links
}

/**
 * Create a manual reference link from URL
 * Extracts domain and generates a placeholder title
 */
export function createManualReferenceLink(url: string): Omit<ReferenceLink, 'id'> {
  const domain = extractDomain(url)
  const citationSource = detectCitationSource(domain)

  return {
    url,
    title: url,
    domain,
    source: 'manual',
    citationSource,
    autoImported: false,
  }
}

/**
 * Generate mock topic suggestions based on input
 */
export function generateMockTopicSuggestions(topic: string): TopicSuggestion[] {
  const baseTopic = topic || 'content strategy'

  return [
    {
      id: 'topic_001',
      title: `The Complete Guide to ${baseTopic} in 2024`,
      description: 'Comprehensive overview covering all essential aspects',
      relevanceScore: 0.95,
      suggestedKeywords: [baseTopic, 'guide', '2024', 'tutorial'],
    },
    {
      id: 'topic_002',
      title: `${baseTopic}: Everything You Need to Know`,
      description: 'Beginner-friendly introduction with practical examples',
      relevanceScore: 0.88,
      suggestedKeywords: [baseTopic, 'beginner', 'examples', 'how-to'],
    },
    {
      id: 'topic_003',
      title: `How to Choose the Right ${baseTopic} Solution`,
      description: 'Comparison framework for evaluating options',
      relevanceScore: 0.82,
      suggestedKeywords: [baseTopic, 'comparison', 'selection', 'evaluation'],
    },
    {
      id: 'topic_004',
      title: `Top 10 ${baseTopic} Tools Compared and Reviewed`,
      description: 'In-depth analysis of leading solutions',
      relevanceScore: 0.79,
      suggestedKeywords: [baseTopic, 'tools', 'comparison', 'review'],
    },
    {
      id: 'topic_005',
      title: `Why ${baseTopic} Matters More Than Ever`,
      description: 'Industry trends and future outlook',
      relevanceScore: 0.75,
      suggestedKeywords: [baseTopic, 'trends', 'future', 'importance'],
    },
  ]
}

/**
 * Extract domain from URL
 */
function extractDomain(url: string): string {
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`)
    return urlObj.hostname.replace('www.', '')
  } catch {
    return 'unknown.com'
  }
}

/**
 * Detect citation source from domain
 */
function detectCitationSource(domain: string): 'blog' | 'reddit' | 'twitter' | 'linkedin' | 'other' {
  const lowerDomain = domain.toLowerCase()

  if (lowerDomain.includes('reddit.com')) return 'reddit'
  if (lowerDomain.includes('twitter.com') || lowerDomain.includes('x.com')) return 'twitter'
  if (lowerDomain.includes('linkedin.com')) return 'linkedin'
  if (
    lowerDomain.includes('blog') ||
    lowerDomain.includes('medium.com') ||
    lowerDomain.includes('dev.to') ||
    lowerDomain.includes('hashnode.com')
  ) return 'blog'

  return 'other'
}

/**
 * Get all available mock citations
 */
export function getAllMockCitations(): ReferenceLink[] {
  return MOCK_CITATIONS.map(c => ({
    ...c,
    id: `mock_${c.id}`,
    source: 'auto' as const,
    autoImported: true,
  }))
}

/**
 * Get mock citations by source type
 */
export function getMockCitationsBySource(source: 'blog' | 'reddit'): ReferenceLink[] {
  return MOCK_CITATIONS
    .filter(c => c.source === source)
    .map(c => ({
      ...c,
      id: `mock_${c.id}`,
      source: 'auto' as const,
      autoImported: true,
    }))
}
